import { ArcolExport, ArcolBuilding, ArcolFloor, ArcolUnitType, ArcolRoomType } from "./types";
import {
  fetchUseGroups,
  fetchIndustries,
  fetchUnitSpecs,
  fetchUnitSpecAssociations,
  createProject,
  createEstimate,
  createFloorGroup,
  createUseGroup,
  addEstimateUseGroups,
  updateEstimateUseGroup,
  createAreaMatrixItem,
  batchUpsertAreaAllocations,
  createUnitType,
  updateUnitType,
  createUnitTypeUnitSpecAssociation,
  createRoom,
} from "../../api/arcol";
import { Result } from "../../api/types";
import { browserStore } from "../../utils/storageKeys";

export interface ImportLogEntry {
  step: string;
  status: "success" | "error" | "skip";
  message: string;
}

export interface ImportResult {
  projectId: string | null;
  estimateId: string | null;
  log: ImportLogEntry[];
}

type ProgressCallback = (step: string, detail: string) => void;

// ─── Grouping helpers ────────────────────────────────────────────────────────

interface AmiRecord {
  amiId: string;
  buildingName: string;
  floor: ArcolFloor;
}

interface GroupedUnitType {
  label: string;
  area: number;
  beds: number;
  baths: number;
  half_baths: number;
  terminal_hvacs: number;
  eugKey: string; // "buildingName::areaType"
  floorUnitTypes: { id: string; value: number }[];
  isSubtype: boolean;
}

interface GroupedRoom {
  label: string;
  area_average: number;
  height: number;
  quantity: number;
  eugKey: string; // "buildingName::areaType"
}

function mode(values: number[]): number {
  const counts = new Map<number, number>();
  for (const v of values) {
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  let best = values[0];
  let bestCount = 0;
  for (const [v, c] of Array.from(counts)) {
    if (c > bestCount) {
      best = v;
      bestCount = c;
    }
  }
  return best;
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function groupUnitTypes(eugMap: Map<string, string>, amiRecords: AmiRecord[]): GroupedUnitType[] {
  // Collect all unit instances grouped by eugKey + label
  // Key: "eugKey::label", Value: array of instances with their AMI context
  const groups = new Map<string, { instances: ArcolUnitType[]; amiIds: string[] }>();

  for (const record of amiRecords) {
    for (const area of record.floor.areas) {
      if (!area.units) continue;
      const eugKey = `${record.buildingName}::${area.type}`;
      if (!eugMap.has(eugKey)) continue;

      for (const unit of area.units) {
        const groupKey = `${eugKey}::${unit.label}`;
        let group = groups.get(groupKey);
        if (!group) {
          group = { instances: [], amiIds: [] };
          groups.set(groupKey, group);
        }
        group.instances.push(unit);
        group.amiIds.push(record.amiId);
      }
    }
  }

  const result: GroupedUnitType[] = [];

  for (const [groupKey, { instances, amiIds }] of Array.from(groups)) {
    const [buildingName, areaType, ...labelParts] = groupKey.split("::");
    const label = labelParts.join("::"); // rejoin in case label contains ::
    const eugKey = `${buildingName}::${areaType}`;

    const areas = instances.map((u: ArcolUnitType) => u.area);
    const canonicalArea = mode(areas);
    const first = instances[0];

    // Count per AMI for floor_unit_types — canonical type gets all instances
    const amiCounts = new Map<string, number>();
    for (let i = 0; i < instances.length; i++) {
      if (instances[i].area === canonicalArea) {
        amiCounts.set(amiIds[i], (amiCounts.get(amiIds[i]) || 0) + 1);
      }
    }

    result.push({
      label,
      area: canonicalArea,
      beds: first.beds ?? 0,
      baths: first.baths ?? 0,
      half_baths: first.half_baths ?? 0,
      terminal_hvacs: first.terminal_hvacs ?? 0,
      eugKey,
      floorUnitTypes: Array.from(amiCounts, ([id, value]) => ({ id, value })),
      isSubtype: false,
    });

    // Create subtypes for area variants
    const variantAreas = new Map<number, { count: number; amiCounts: Map<string, number> }>();
    for (let i = 0; i < instances.length; i++) {
      const a = instances[i].area;
      if (a === canonicalArea) continue;
      let variant = variantAreas.get(a);
      if (!variant) {
        variant = { count: 0, amiCounts: new Map() };
        variantAreas.set(a, variant);
      }
      variant.count++;
      variant.amiCounts.set(amiIds[i], (variant.amiCounts.get(amiIds[i]) || 0) + 1);
    }

    for (const [variantArea, { amiCounts: vAmiCounts }] of Array.from(variantAreas)) {
      result.push({
        label: `${label} (${variantArea}sf)`,
        area: variantArea,
        beds: first.beds ?? 0,
        baths: first.baths ?? 0,
        half_baths: first.half_baths ?? 0,
        terminal_hvacs: first.terminal_hvacs ?? 0,
        eugKey,
        floorUnitTypes: Array.from(vAmiCounts, ([id, value]) => ({ id, value })),
        isSubtype: true,
      });
    }
  }

  return result;
}

function groupRooms(eugMap: Map<string, string>, amiRecords: AmiRecord[]): GroupedRoom[] {
  // Key: "eugKey::label", Value: array of room instances
  const groups = new Map<string, ArcolRoomType[]>();

  for (const record of amiRecords) {
    for (const area of record.floor.areas) {
      if (!area.rooms) continue;
      const eugKey = `${record.buildingName}::${area.type}`;
      if (!eugMap.has(eugKey)) continue;

      for (const room of area.rooms) {
        const groupKey = `${eugKey}::${room.label}`;
        let group = groups.get(groupKey);
        if (!group) {
          group = [];
          groups.set(groupKey, group);
        }
        group.push(room);
      }
    }
  }

  const result: GroupedRoom[] = [];

  for (const [groupKey, instances] of Array.from(groups)) {
    const [buildingName, areaType, ...labelParts] = groupKey.split("::");
    const label = labelParts.join("::");
    const eugKey = `${buildingName}::${areaType}`;

    result.push({
      label,
      area_average: mean(instances.map((r: ArcolRoomType) => r.area)),
      height: mean(instances.filter((r: ArcolRoomType) => r.height != null).map((r: ArcolRoomType) => r.height!)),
      quantity: instances.length,
      eugKey,
    });
  }

  return result;
}

// ─── Import pipeline ─────────────────────────────────────────────────────────

function collectUniqueAreaTypes(buildings: ArcolBuilding[]): Map<string, string> {
  const typeToCategory = new Map<string, string>();
  for (const building of buildings) {
    for (const floor of building.floors) {
      for (const area of floor.areas) {
        if (!typeToCategory.has(area.type)) {
          typeToCategory.set(area.type, area.category?.toUpperCase() || "GENERAL");
        }
      }
    }
  }
  return typeToCategory;
}

function getErrorMessage(result: Result<any>): string {
  if (!result.success) {
    return result.error?.message || "Unknown error";
  }
  return "Unknown error";
}

export async function importArcolData(
  data: ArcolExport,
  regionId: string,
  onProgress?: ProgressCallback
): Promise<ImportResult> {
  const log: ImportLogEntry[] = [];
  let projectId: string | null = null;
  let estimateId: string | null = null;

  const progress = (step: string, detail: string) => {
    if (onProgress) onProgress(step, detail);
  };

  try {
    // ── Step 1: Look up industry ──────────────────────────────────────────
    progress("Setup", "Looking up industry...");
    const industriesResult = await fetchIndustries();
    let industryId: string | null = null;
    if (industriesResult.success && industriesResult.data && industriesResult.data.length > 0) {
      if (data.industry) {
        const match = industriesResult.data.find((i) => i.label.toLowerCase() === data.industry!.toLowerCase());
        if (match) {
          industryId = match.value;
          log.push({ step: "Setup", status: "success", message: `Matched industry: ${match.label}` });
        } else {
          industryId = industriesResult.data[0].value;
          log.push({
            step: "Setup",
            status: "success",
            message: `Industry "${data.industry}" not found, using: ${industriesResult.data[0].label}`,
          });
        }
      } else {
        industryId = industriesResult.data[0].value;
        log.push({ step: "Setup", status: "success", message: `Using industry: ${industriesResult.data[0].label}` });
      }
    } else {
      log.push({ step: "Setup", status: "skip", message: "No industries found, proceeding without" });
    }

    // ── Step 2: Look up existing use groups ──────────────────────────────
    progress("Setup", "Looking up use groups...");
    const useGroupsResult = await fetchUseGroups();
    const existingUseGroups: Map<string, string> = new Map();
    if (useGroupsResult.success && useGroupsResult.data) {
      for (const ug of useGroupsResult.data) {
        existingUseGroups.set(ug.name.toLowerCase(), ug.id);
      }
    }

    // Fetch unit specs + spec associations for beds/baths/half_baths display
    const specNameToField: Record<string, "beds" | "baths" | "half_baths" | "terminal_hvacs"> = {
      Beds: "beds",
      Baths: "baths",
      "Half Baths": "half_baths",
      "Terminal HVAC": "terminal_hvacs",
    };
    // Map: useGroupId → Map<fieldName, specAssociationId>
    const useGroupSpecAssocs: Map<string, Map<string, string>> = new Map();

    const [unitSpecsResult, specAssocsResult] = await Promise.all([fetchUnitSpecs(), fetchUnitSpecAssociations()]);
    if (unitSpecsResult.success && unitSpecsResult.data && specAssocsResult.success && specAssocsResult.data) {
      const specIdToName = new Map<string, string>();
      for (const spec of unitSpecsResult.data) {
        specIdToName.set(spec.id, spec.name);
      }
      for (const assoc of specAssocsResult.data) {
        const specName = specIdToName.get(assoc.unit_spec);
        const fieldName = specName ? specNameToField[specName] : undefined;
        if (!fieldName) continue;
        let ugMap = useGroupSpecAssocs.get(assoc.use_group);
        if (!ugMap) {
          ugMap = new Map();
          useGroupSpecAssocs.set(assoc.use_group, ugMap);
        }
        ugMap.set(fieldName, assoc.id);
      }
      log.push({
        step: "Setup",
        status: "success",
        message: `Loaded unit spec associations for ${useGroupSpecAssocs.size} use groups`,
      });
    }

    const areaTypeCategories = collectUniqueAreaTypes(data.buildings);
    const neededAreaTypes = Array.from(areaTypeCategories.keys());
    log.push({
      step: "Setup",
      status: "success",
      message: `Found ${existingUseGroups.size} existing use groups, need ${neededAreaTypes.length} area types`,
    });

    // ── Step 3: Create missing use groups ────────────────────────────────
    progress("Use Groups", "Creating missing use groups...");
    if (data.site) {
      areaTypeCategories.set("Site Development", "SITE_INFRASTRUCTURE");
    }
    const useGroupMap: Map<string, string> = new Map();
    for (const [areaType, category] of Array.from(areaTypeCategories.entries())) {
      const existing = existingUseGroups.get(areaType.toLowerCase());
      if (existing) {
        useGroupMap.set(areaType, existing);
      } else {
        const result = await createUseGroup(areaType, category);
        if (result.success && result.data) {
          useGroupMap.set(areaType, result.data);
          log.push({ step: "Use Groups", status: "success", message: `Created use group: ${areaType}` });
        } else {
          log.push({
            step: "Use Groups",
            status: "error",
            message: `Failed to create use group "${areaType}": ${getErrorMessage(result)}`,
          });
        }
      }
    }

    // ── Step 4: Create project ───────────────────────────────────────────
    progress("Project", "Creating project...");
    let ownerId: string | undefined;
    try {
      const profile = JSON.parse(browserStore.get("userProfile") || "{}");
      if (profile.id) ownerId = profile.id;
    } catch {
      /* no-op — owner will default to API behavior */
    }

    const projectResult = await createProject({
      name: data.project_name,
      client_name: data.project_name,
      region: regionId,
      industry: industryId || "",
      city: "Unknown",
      state: "XX",
      ...(ownerId && { owner: ownerId }),
    });

    if (!projectResult.success || !projectResult.data) {
      log.push({
        step: "Project",
        status: "error",
        message: `Failed to create project: ${getErrorMessage(projectResult)}`,
      });
      return { projectId: null, estimateId: null, log };
    }
    projectId = projectResult.data;
    log.push({ step: "Project", status: "success", message: `Created project: ${data.project_name} (${projectId})` });

    // ── Step 5: Create estimate ──────────────────────────────────────────
    progress("Estimate", "Creating estimate...");
    const estimateResult = await createEstimate({
      name: `${data.project_name} - Arcol Import`,
      project: projectId,
      active: true,
      primary: true,
    });
    if (!estimateResult.success || !estimateResult.data) {
      log.push({
        step: "Estimate",
        status: "error",
        message: `Failed to create estimate: ${getErrorMessage(estimateResult)}`,
      });
      return { projectId, estimateId: null, log };
    }
    estimateId = estimateResult.data;
    log.push({ step: "Estimate", status: "success", message: `Created estimate (${estimateId})` });

    // ── Step 6: Create floor groups (buildings) ──────────────────────────
    progress("Buildings", "Creating buildings...");
    const floorGroupMap: Map<string, string> = new Map();
    for (const building of data.buildings) {
      const fgResult = await createFloorGroup({ estimate: estimateId, label: building.name });
      if (fgResult.success && fgResult.data) {
        floorGroupMap.set(building.name, fgResult.data);
        log.push({ step: "Buildings", status: "success", message: `Created building: ${building.name}` });
      } else {
        log.push({
          step: "Buildings",
          status: "error",
          message: `Failed to create building "${building.name}": ${getErrorMessage(fgResult)}`,
        });
      }
    }

    // ── Step 7: Add use groups to estimate (per building) ─────────────────
    progress("Estimate Use Groups", "Adding use groups to estimate...");
    // Key: "buildingName::areaType", Value: EUG ID
    const eugMap: Map<string, string> = new Map();

    for (const building of data.buildings) {
      const floorGroupId = floorGroupMap.get(building.name);
      const buildingAreaTypes = new Map<string, string | undefined>();
      for (const floor of building.floors) {
        for (const area of floor.areas) {
          if (!buildingAreaTypes.has(area.type)) {
            buildingAreaTypes.set(area.type, area.label);
          }
        }
      }

      for (const [areaType, explicitLabel] of Array.from(buildingAreaTypes.entries())) {
        const ugId = useGroupMap.get(areaType);
        if (!ugId) continue;

        const addResult = await addEstimateUseGroups(estimateId, [{ use_group: ugId, quantity: 1 }]);
        if (!addResult.success || !addResult.data) {
          log.push({
            step: "Estimate Use Groups",
            status: "error",
            message: `Failed to add use group "${areaType}" for ${building.name}: ${getErrorMessage(addResult)}`,
          });
          continue;
        }

        if (addResult.data.length === 0) {
          log.push({
            step: "Estimate Use Groups",
            status: "error",
            message: `API returned empty EUG list for "${areaType}" in ${building.name}`,
          });
          continue;
        }
        const newEug = addResult.data.reduce((max, eug) => (eug.order > max.order ? eug : max));
        const eugId = newEug.id;

        const eugLabel = explicitLabel || `${building.name} - ${areaType}`;
        const eugUpdate: { id: string; label: string; floor_group?: string } = {
          id: eugId,
          label: eugLabel,
        };
        if (floorGroupId) eugUpdate.floor_group = floorGroupId;

        const updateResult = await updateEstimateUseGroup(estimateId, [eugUpdate]);
        if (!updateResult.success) {
          log.push({
            step: "Estimate Use Groups",
            status: "error",
            message: `Failed to update "${areaType}" for ${building.name}: ${getErrorMessage(updateResult)}`,
          });
        }

        eugMap.set(`${building.name}::${areaType}`, eugId);
        log.push({ step: "Estimate Use Groups", status: "success", message: `Added: ${areaType} → ${building.name}` });
      }
    }

    // ── Site Development EUG (not tied to a building, exclude_area=true) ──
    if (data.site) {
      const siteUgId = useGroupMap.get("Site Development");
      if (siteUgId) {
        const addResult = await addEstimateUseGroups(estimateId, [{ use_group: siteUgId, quantity: 1 }]);
        if (addResult.success && addResult.data && addResult.data.length > 0) {
          const newEug = addResult.data.reduce((max, eug) => (eug.order > max.order ? eug : max));
          const siteEugId = newEug.id;
          const updateResult = await updateEstimateUseGroup(estimateId, [
            { id: siteEugId, label: "Site Development", exclude_area: true },
          ]);
          if (updateResult.success) {
            log.push({
              step: "Estimate Use Groups",
              status: "success",
              message: "Added: Site Development (exclude_area)",
            });
          } else {
            log.push({
              step: "Estimate Use Groups",
              status: "error",
              message: `Failed to update Site Development EUG: ${getErrorMessage(updateResult)}`,
            });
          }

          const siteAmiResult = await createAreaMatrixItem({
            estimate: estimateId,
            floor_label: "Site",
            floor_number: 1,
            floor_type: "FEATURE",
          });
          if (siteAmiResult.success && siteAmiResult.data) {
            const batchResult = await batchUpsertAreaAllocations(estimateId, siteAmiResult.data, [
              {
                area_matrix_item: siteAmiResult.data,
                estimate_use_group: siteEugId,
                area: data.site.total_area,
                active_floor: true,
              },
            ]);
            if (batchResult.success) {
              log.push({
                step: "Estimate Use Groups",
                status: "success",
                message: `Site Development: ${data.site.total_area} sf allocated`,
              });
            } else {
              log.push({
                step: "Estimate Use Groups",
                status: "error",
                message: `Failed to allocate site area: ${getErrorMessage(batchResult)}`,
              });
            }
          } else {
            log.push({
              step: "Estimate Use Groups",
              status: "error",
              message: `Failed to create site AMI: ${getErrorMessage(siteAmiResult)}`,
            });
          }
        } else {
          log.push({
            step: "Estimate Use Groups",
            status: "error",
            message: `Failed to add Site Development use group: ${getErrorMessage(addResult)}`,
          });
        }
      }
    }

    // ── Step 8: Create area matrix items (floors) + allocations ──────────
    progress("Area Sheets", "Creating floors and allocations...");
    const amiRecords: AmiRecord[] = [];

    for (const building of data.buildings) {
      const floorGroupId = floorGroupMap.get(building.name);

      for (const floor of building.floors) {
        const floorCount = floor.floor_count || 1;

        for (let i = 0; i < floorCount; i++) {
          const floorLabel = floorCount > 1 ? `Level ${floor.floor_number + i}` : floor.label;
          const floorNumber = floor.floor_number + i;

          const amiPayload = {
            estimate: estimateId,
            floor_label: floorLabel,
            floor_number: floorNumber,
            floor_type: floor.floor_type.toUpperCase(),
            ...(floorGroupId && { floor_group: floorGroupId }),
            ...(floor.facade_perimeter && { facade_perimeter: floor.facade_perimeter }),
            ...(floor.facade_floor_to_floor && { facade_floor_to_floor: floor.facade_floor_to_floor }),
          };

          const amiResult = await createAreaMatrixItem(amiPayload);
          if (!amiResult.success || !amiResult.data) {
            log.push({
              step: "Area Sheets",
              status: "error",
              message: `Failed to create floor "${floorLabel}" in ${building.name}: ${getErrorMessage(amiResult)}`,
            });
            continue;
          }
          const amiId = amiResult.data;
          amiRecords.push({ amiId, buildingName: building.name, floor });

          const allocations = floor.areas
            .map((area) => {
              const eugId = eugMap.get(`${building.name}::${area.type}`);
              if (!eugId) return null;
              return {
                area_matrix_item: amiId,
                estimate_use_group: eugId,
                area: area.area,
                active_floor: true,
              };
            })
            .filter((a): a is NonNullable<typeof a> => a !== null);

          if (allocations.length > 0) {
            const batchResult = await batchUpsertAreaAllocations(estimateId, amiId, allocations);
            if (!batchResult.success) {
              log.push({
                step: "Area Sheets",
                status: "error",
                message: `Failed to set allocations for "${floorLabel}": ${getErrorMessage(batchResult)}`,
              });
            }
          }

          log.push({
            step: "Area Sheets",
            status: "success",
            message: `${building.name} / ${floorLabel}: ${allocations.length} allocations`,
          });
        }
      }
    }

    // ── Step 9: Create unit types (from inline per-floor data) ──────────
    const groupedUnits = groupUnitTypes(eugMap, amiRecords);
    if (groupedUnits.length > 0) {
      progress("Unit Types", "Creating unit types...");

      for (const unitDef of groupedUnits) {
        const eugId = eugMap.get(unitDef.eugKey);
        if (!eugId) continue;

        const result = await createUnitType({
          estimate: estimateId,
          label: unitDef.label,
          estimate_use_group: eugId,
          area: unitDef.area,
          beds: unitDef.beds,
          baths: unitDef.baths,
          half_baths: unitDef.half_baths,
        });

        if (!result.success || !result.data) {
          log.push({
            step: "Unit Types",
            status: "error",
            message: `Failed to create "${unitDef.label}": ${getErrorMessage(result)}`,
          });
          continue;
        }

        const unitTypeId = result.data;

        // PUT beds/baths/half_baths/terminal_hvacs + floor_unit_types
        const updatePayload: {
          beds?: number;
          baths?: number;
          half_baths?: number;
          terminal_hvacs?: number;
          extras?: Record<string, unknown>;
        } = {};
        if (unitDef.beds) updatePayload.beds = unitDef.beds;
        if (unitDef.baths) updatePayload.baths = unitDef.baths;
        if (unitDef.half_baths) updatePayload.half_baths = unitDef.half_baths;
        if (unitDef.terminal_hvacs) updatePayload.terminal_hvacs = unitDef.terminal_hvacs;
        if (unitDef.floorUnitTypes.length > 0) {
          updatePayload.extras = { floor_unit_types: unitDef.floorUnitTypes };
        }

        if (Object.keys(updatePayload).length > 0) {
          const mixResult = await updateUnitType(unitTypeId, updatePayload);
          if (!mixResult.success) {
            log.push({
              step: "Unit Types",
              status: "error",
              message: `Failed to update "${unitDef.label}": ${getErrorMessage(mixResult)}`,
            });
          }
        }

        // Create unit_type_unit_spec_associations for display
        const [, areaType] = unitDef.eugKey.split("::");
        const ugId = useGroupMap.get(areaType);
        const specAssocs = ugId ? useGroupSpecAssocs.get(ugId) : undefined;
        if (specAssocs) {
          const specFields: { field: "beds" | "baths" | "half_baths" | "terminal_hvacs"; value: number }[] = [
            { field: "beds", value: unitDef.beds },
            { field: "baths", value: unitDef.baths },
            { field: "half_baths", value: unitDef.half_baths },
            { field: "terminal_hvacs", value: unitDef.terminal_hvacs },
          ];
          for (const { field, value } of specFields) {
            if (!value) continue;
            const specAssocId = specAssocs.get(field);
            if (!specAssocId) continue;
            await createUnitTypeUnitSpecAssociation(unitTypeId, specAssocId, value);
          }
        }

        const totalUnits = unitDef.floorUnitTypes.reduce((sum, f) => sum + f.value, 0);
        const subtypeTag = unitDef.isSubtype ? " [subtype]" : "";
        log.push({
          step: "Unit Types",
          status: "success",
          message: `Created: "${unitDef.label}"${subtypeTag} (${unitTypeId}, ${totalUnits} units across ${unitDef.floorUnitTypes.length} floors)`,
        });
      }
    }

    // ── Step 10: Create rooms (from inline per-floor data) ───────────────
    const groupedRoomDefs = groupRooms(eugMap, amiRecords);
    if (groupedRoomDefs.length > 0) {
      progress("Rooms", "Creating rooms...");

      for (const roomDef of groupedRoomDefs) {
        const eugId = eugMap.get(roomDef.eugKey);
        if (!eugId) continue;

        const result = await createRoom({
          estimate: estimateId,
          label: roomDef.label,
          estimate_use_group: eugId,
          area_average: roomDef.area_average,
          height: roomDef.height || undefined,
          quantity: roomDef.quantity,
        });

        if (result.success && result.data) {
          log.push({
            step: "Rooms",
            status: "success",
            message: `Created: "${roomDef.label}" → qty ${roomDef.quantity}, avg ${Math.round(roomDef.area_average)} sf (${result.data})`,
          });
        } else {
          log.push({
            step: "Rooms",
            status: "error",
            message: `Failed to create "${roomDef.label}": ${getErrorMessage(result)}`,
          });
        }
      }
    }

    progress("Complete", "Import finished");
    log.push({
      step: "Complete",
      status: "success",
      message: `Import complete. Project: ${projectId}, Estimate: ${estimateId}`,
    });
  } catch (error) {
    log.push({
      step: "Fatal",
      status: "error",
      message: `Unexpected error: ${error instanceof Error ? error.message : String(error)}`,
    });
  }

  return { projectId, estimateId, log };
}
